import { Divider } from "@mui/material";
import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { hasUniqueID } from "../../shared/helpers/preOrder";
import NextOrSkipButtons from "../../shared/ui/components/NextOrSkipButtons";
import Spinner from "../../shared/ui/components/Spinner";
import Card from "../../shared/ui/containers/GiftCard/index";
import { getPreOrder, updatePreOrder } from "../../store/slicers/preorder";
import {
  getGiftCards,
  selectGiftCards,
  updateGiftCardList,
  updateSelectedCardsList,
} from "../../store/slicers/template";
import useStyles from "./style";

interface IInitialState {
  openedGiftCardId: number | null;
  selectedCardIds: any | null;
  openedCardId: number | null;
}

const initialState: IInitialState = {
  openedGiftCardId: 0,
  selectedCardIds: null,
  openedCardId: 0,
};

enum ECompanyStatuses {
  BOOKS = "Books",
  SWEETS = "Sweets",
  GIFT_CARDS = "Gift cards",
  TOYS = "Toys",
  WINTER_PARK = "Winter park",
}

const reducer = (
  { openedGiftCardId, selectedCardIds, openedCardId }: IInitialState,
  { type, payload }: { type: string; payload: any }
): IInitialState => {
  switch (type) {
    case "selectGift": {
      if (openedGiftCardId === payload.id) {
        return {
          selectedCardIds,
          openedGiftCardId: null,
          openedCardId: null,
        };
      } else {
        return {
          selectedCardIds,
          openedGiftCardId: payload.id,
          openedCardId: payload.cardID,
        };
      }
    }
    case "selectCard": {
      return {
        openedGiftCardId,
        selectedCardIds,
        openedCardId: payload,
      };
    }
    case "setCardIds":
      return {
        selectedCardIds: payload,
        openedGiftCardId,
        openedCardId,
      };
    case "selectedCardIds": {
      return {
        selectedCardIds: {
          [payload.groupID]: payload.cardID,
        },
        openedGiftCardId: null,
        openedCardId: null,
      };
    }
    default:
      return {
        selectedCardIds,
        openedGiftCardId,
        openedCardId,
      };
  }
};

const GiftCard = (): JSX.Element => {
  const [reducerState, dispatchAction] = useReducer(reducer, initialState);
  const [loaded, setLoaded] = useState<boolean>(false);
  const styles = useStyles();
  const history = useHistory();
  const giftCards = useSelector(selectGiftCards);
  const dispatch = useDispatch<any>();

  const skipBtnHandler = () => {
    dispatch(updateGiftCardList([]));
    const preOrderDataWithoutGiftCard = {
      key: Cookies.get("uniqueId"),
      route: "/letter",
      json: JSON.stringify({ giftCards: [] }).replace(/"/g, "¶œÆ"),
    };
    if (hasUniqueID()) {
      dispatch(updatePreOrder(preOrderDataWithoutGiftCard));
    }
  };

  const handleSelectGiftCards = async () => {
    dispatch(updateSelectedCardsList(reducerState.selectedCardIds));
    const arr = Object.values(reducerState.selectedCardIds);
    const data = {
      key: Cookies.get("uniqueId"),
      route: `/addresses`,
      json: JSON.stringify({
        giftCards,
        selectedGiftCardIDs: reducerState?.selectedCardIds,
      }).replace(/"/g, "¶œÆ"),
    };

    if (hasUniqueID()) {
      const { meta } = await dispatch(updatePreOrder(data));
      if (meta.requestStatus !== "fulfilled") {
        return;
      }
      dispatch(getPreOrder(Cookies.get("uniqueId")));

      history.push(`/addresses`);
    }
  };

  const filteredWinterParkList = useMemo(() => {
    const filteredWinterParks = giftCards?.filter(
      (item: any) => item.status === ECompanyStatuses.GIFT_CARDS
    );
    return filteredWinterParks;
  }, [giftCards]);

  const initialFetch = useCallback(async () => {
    setLoaded(false);
    const { payload } = await dispatch(getPreOrder(Cookies.get("uniqueId")));
    if (Array.isArray(payload)) {
      const giftCardsData = payload.find((item) => item.route === "/addresses");
      if (giftCardsData) {
        const parsedData = giftCardsData?.json.replace(/¶œÆ/g, '"');
        const parsedGiftCardsData = JSON.parse(parsedData).selectedGiftCardIDs;
        dispatchAction({
          type: "setCardIds",
          payload: parsedGiftCardsData,
        });
      }
    }
    await dispatch(getGiftCards());
    setTimeout(() => {
      setLoaded(true);
    }, 200);
  }, [dispatch]);

  useEffect(() => {
    initialFetch();
  }, [initialFetch]);

  useEffect(() => {
    return () => setLoaded(false);
  }, []);

  return (
    <div className={styles.root}>
      <div className={styles.row}>
        <div className={styles.pageTextArea}>
          <p className={styles.pageTitle}>Ընտրիր քո նախընտրած բացիկը </p>
        </div>
        <NextOrSkipButtons
          nextPagePath="addresses"
          nextOrSkipBtnAction={skipBtnHandler}
          isChooseButtonVisible
          handleChooseGiftCards={handleSelectGiftCards}
          chooseButtonDisabled={!reducerState?.selectedCardIds}
          hasBackButton={false}
          hasNextButton={false}
        />
      </div>
      {loaded ? (
        <>
          {filteredWinterParkList?.length ? (
            <>
              <Divider className={styles.dividerStyles} />

              <div className={styles.giftCardsRow}>
                <Card
                  id="companyID"
                  imgAltText="gift-card"
                  data={filteredWinterParkList}
                  itemDetailRowClassName="gift-card-item-detail-row"
                  cardInnerData="giftCards"
                  cardInnerItemsAltText="active-gift"
                  reducer={reducerState}
                  dispatchAction={dispatchAction}
                  setLoaded={setLoaded}
                />
              </div>
            </>
          ) : null}

          <NextOrSkipButtons
            nextPagePath="addresses"
            wrapperClassName={styles.buttonsArea}
            nextOrSkipBtnAction={skipBtnHandler}
            isChooseButtonVisible
            handleChooseGiftCards={handleSelectGiftCards}
            chooseButtonDisabled={!reducerState?.selectedCardIds}
            hasBackButton={false}
            hasNextButton={false}
          />
        </>
      ) : (
        <div className={styles.spinnerWrapper}>
          <Spinner size={50} />
        </div>
      )}
    </div>
  );
};

export default GiftCard;
